import { FileText, Pencil, Quote } from 'lucide-react'
import { PORTRAIT_FIELDS, type PortraitFieldKey } from '../../lib/authorInsights'
import { formatAgo } from '../../lib/format'
import { Button } from '../Button'
import { Card, Section } from '../Section'
import type { PortraitFieldView } from './viewModels'

export const portraitStatus = (status: string, overridden = false) => overridden || status === 'confirmed'
  ? 'Подтверждено командой'
  : status === 'declared'
    ? 'Заявлено в источнике'
    : status === 'inferred' || status === 'hypothesis'
      ? 'Предположение по контенту'
      : 'Мало данных'

export function AuthorPortrait({ fields, profileAt, contentAt, analysisAt, analyzed, total, canEdit, onEdit, onEvidence, hasStaleEvidence = false, needsEvidenceRefresh = false }: {
  fields: PortraitFieldView[]
  profileAt: string | null
  contentAt: string | null
  analysisAt: string | null
  analyzed: number
  total: number
  canEdit: boolean
  onEdit: () => void
  onEvidence: (field: PortraitFieldKey) => void
  hasStaleEvidence?: boolean
  needsEvidenceRefresh?: boolean
}) {
  return <Section title="Кому и зачем этот автор" aside={canEdit && <Button size="sm" variant="ghost" onClick={onEdit}><Pencil size={14} />Изменить</Button>} className="mb-6">
    <Card className="p-4 desk:p-5">
      {hasStaleEvidence && <p className="mb-4 rounded-lg bg-surface px-3 py-2 text-xs text-muted" role="note">Часть оснований AI-разбора историческая или требует проверки. Даты и цитаты сохранены в примерах.</p>}
      {needsEvidenceRefresh && <p className="mb-4 rounded-lg bg-surface px-3 py-2 text-xs text-muted" role="note">Доступен новый разбор. Обновите карточку для проверенных таймкодов.</p>}
      <dl className="grid gap-4 desk:grid-cols-2 desk:gap-x-8 desk:gap-y-5">
        {PORTRAIT_FIELDS.map(([key, label]) => {
          const field = fields.find((entry) => entry.key === key)
          return <div key={key} className="min-w-0">
            <dt className="mb-1.5 text-xs font-medium text-muted">{label}</dt>
            <dd>
              <p className={`whitespace-pre-wrap break-words text-sm leading-relaxed ${field?.value ? '' : 'text-muted'}`}>{field?.value || 'Пока недостаточно подтверждающих материалов.'}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[11px] text-muted">{portraitStatus(field?.status ?? 'missing', field?.overridden)}</span>
                {Boolean(field?.evidence.length) && <button type="button" className="inline-flex min-h-9 items-center gap-1 text-xs text-link desk:min-h-7" onClick={() => onEvidence(key)}><Quote size={12} />{field?.overridden ? 'Примеры исходного AI-разбора' : 'Примеры'} · {field!.evidence.length}</button>}
              </div>
            </dd>
          </div>
        })}
      </dl>
      <div className="mt-4 border-t border-line pt-3 text-xs leading-relaxed text-muted">
        <p className="flex items-start gap-1.5"><FileText size={13} className="mt-0.5 shrink-0" />Портрет по профилю и опубликованному в нём контенту последних 90 дней. Репосты в нём не учитываются. Разобрано публикаций: {analyzed} из {total}.</p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <span>Профиль: {profileAt ? formatAgo(profileAt) : 'ещё не получен'}</span>
          <span>Контент: {contentAt ? formatAgo(contentAt) : 'нет данных'}</span>
          <span>Анализ: {analysisAt ? formatAgo(analysisAt) : 'ещё не готов'}</span>
        </div>
      </div>
    </Card>
  </Section>
}
